import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import ResizeObserver from 'resize-observer-polyfill';
import { Theme } from '../components/Theme/Theme.js';
import { useThemeContext } from '../styles/themes/themes.js';
import { bindRefs } from '../utils/react.js';
import { jsx } from 'react/jsx-runtime';

var containerStyle = {
  display: "inline-block",
  position: "absolute",
  visibility: "hidden",
  zIndex: -1
};
var useResponsiveChildren = function useResponsiveChildren(_ref) {
  var children = _ref.children,
    containerSelector = _ref.containerSelector,
    _ref$getRefProp = _ref.getRefProp,
    getRefProp = _ref$getRefProp === void 0 ? function () {
      return "ref";
    } : _ref$getRefProp,
    parentRef = _ref.parentRef,
    preservedIndex = _ref.preservedIndex;
  var _React$useState = React.useState([[], []]),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    result = _React$useState2[0],
    setResult = _React$useState2[1];
  var theme = useThemeContext();
  var childrenLength = React.Children.toArray(children).length;
  var itemRefs = React.useMemo(function () {
    return Array.from({
      length: childrenLength
    }, function () {
      return /*#__PURE__*/React.createRef();
    });
  }, [childrenLength]);
  var rootSelector = containerSelector || theme.defaultPortalContainerSelector || "body";
  var getRefPropRef = React.useRef(getRefProp);
  var setChildren = React.useCallback(function () {
    var childrenArray = React.Children.toArray(children);
    var container = document.createElement("div");
    Object.assign(container.style, containerStyle);
    container.setAttribute("aria-hidden", "true");
    var root = document.querySelector(rootSelector);
    root === null || root === void 0 ? void 0 : root.appendChild(container);
    var element = React.Children.map(children, function (child, index) {
      var refProp = getRefPropRef.current(child);
      return /*#__PURE__*/React.cloneElement(child, _defineProperty({}, refProp, bindRefs(itemRefs[index], child[refProp])));
    });
    var elementWithTheme = /*#__PURE__*/jsx(Theme, {
      theme: theme,
      children: element
    });
    render(elementWithTheme, container, function () {
      var currentWidth = (preservedIndex || []).reduce(function (w, index) {
        var _itemRefs$index, _itemRefs$index$curre;
        return w + (((_itemRefs$index = itemRefs[index]) === null || _itemRefs$index === void 0 ? void 0 : (_itemRefs$index$curre = _itemRefs$index.current) === null || _itemRefs$index$curre === void 0 ? void 0 : _itemRefs$index$curre.getBoundingClientRect().width) || 0);
      }, 0);
      var hideAtIndex;
      setResult(itemRefs.reduce(function (arr, item, index) {
        var _item$current, _parentRef$current;
        var child = childrenArray[index];
        if ((preservedIndex || []).includes(index)) {
          arr[0].push(child);
          return arr;
        }
        if (hideAtIndex !== void 0 && index > hideAtIndex) {
          arr[1].push(child);
          return arr;
        }
        var nextWidth = currentWidth + Math.ceil(((_item$current = item.current) === null || _item$current === void 0 ? void 0 : _item$current.getBoundingClientRect().width) || 0);
        var parentWidth = Math.ceil((parentRef === null || parentRef === void 0 ? void 0 : (_parentRef$current = parentRef.current) === null || _parentRef$current === void 0 ? void 0 : _parentRef$current.getBoundingClientRect().width) || 0);
        if (nextWidth <= parentWidth) {
          currentWidth = nextWidth;
          arr[0].push(child);
        } else {
          hideAtIndex = index;
          arr[1].push(child);
        }
        return arr;
      }, [[], []]));
      unmountComponentAtNode(container);
      root === null || root === void 0 ? void 0 : root.removeChild(container);
    });
  }, [children, rootSelector, theme, itemRefs, preservedIndex, parentRef]);
  React.useLayoutEffect(function () {
    setChildren();
    var resizeObserver = new ResizeObserver(function () {
      setChildren();
    });
    if (parentRef.current) resizeObserver.observe(parentRef.current);
    return function () {
      return resizeObserver.disconnect();
    };
  }, [setChildren, parentRef]);
  return result;
};

export { useResponsiveChildren };
